import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { ApiError } from '../../api/client';
import { b2bApi, b2bOfferTypesFor, b2bOpenUrl, type B2bKind, type B2bOfferType, type B2bOutput, type B2bStatus, type B2bView } from '../../api/b2b';
import { DialogShell, confirmDialog, noticeDialog } from '../../shell/dialogs';
import { ServiceBar, type ServiceBarSelect } from '../../shell/ServiceBar';
import { useFeather } from '../../shell/useFeather';
import { ArcDialog, ArcPage, ArcTableFrame } from '../../components/arc/primitives/arc-primitives';
import './stranky.css';

const KIND_LABEL: Record<B2bKind, string> = { offer: 'Nabídka', report: 'Report', card: 'Vizitka', guide: 'Návod' };
const STATUS_LABEL: Record<B2bStatus, string> = { draft: 'Koncept', active: 'Aktivní', archived: 'Archiv' };
const TYPE_CLASS: Record<B2bOfferType, string> = { 'Land-based': 'land', 'Affiliate service': 'affiliate', 'Pozvánka': 'pozvanka', iGaming: 'igaming', Performance: 'module', Multibrand: 'module', 'AI asistent': 'module' };

function label(o: B2bOutput): string {
  return o.kind === 'report' ? o.title : o.clientName ?? o.title;
}

function fmtDay(v: string | null): string {
  if (!v) return '';
  const [y, m, d] = v.split('-').map(Number);
  return `${d}. ${m}. ${y}`;
}

const MODULE_MARK: Partial<Record<B2bOfferType, string>> = { Performance: '+', Multibrand: 'M', 'AI asistent': 'AI' };

function OfferTypes({ types }: { types: B2bOfferType[] }): JSX.Element {
  if (!types.length) return <span className="text-muted">nezadáno</span>;
  const { modules } = b2bOfferTypesFor();
  const main = types.filter((t) => !modules.includes(t));
  const extra = types.filter((t) => modules.includes(t));
  return (
    <span className="st-otypes">
      {main.map((t) => <span key={t} className={`st-otype st-otype--${TYPE_CLASS[t]}`}>{t}</span>)}
      {extra.length ? <span className="st-omods">{extra.map((t) => <span key={t} className="st-omod" title={t} aria-label={t}>{MODULE_MARK[t] ?? t}</span>)}</span> : null}
    </span>
  );
}

interface EditState { types: B2bOfferType[]; publishedAt: string; accessCode: string }

const CODE_ALPHABET = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';

function newAccessCode(): string {
  const bytes = new Uint8Array(8);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => CODE_ALPHABET[b % CODE_ALPHABET.length]).join('');
}

function hasAccessCode(o: B2bOutput): boolean {
  return o.kind === 'offer' && typeof o.accessCode === 'string' && o.accessCode.length > 0;
}

function EditDialog({ output, onClose, onSaved }: { output: B2bOutput; onClose: () => void; onSaved: () => Promise<void> }): JSX.Element {
  const withCode = hasAccessCode(output);
  const typeSet = b2bOfferTypesFor();
  const [form, setForm] = useState<EditState>({ types: output.offerTypes, publishedAt: output.publishedAt ?? '', accessCode: output.accessCode ?? '' });
  const [saving, setSaving] = useState(false);
  const modulesOn = typeSet.moduleParent !== null && form.types.includes(typeSet.moduleParent);
  const toggle = (t: B2bOfferType) => setForm((f) => {
    let types = f.types.includes(t) ? f.types.filter((x) => x !== t) : [...f.types, t];
    if (typeSet.moduleParent && t === typeSet.moduleParent && !types.includes(t)) types = types.filter((x) => !typeSet.modules.includes(x));
    return { ...f, types };
  });
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const code = form.accessCode.replace(/\s+/g, '').toUpperCase();
    if (withCode && !/^[A-Z0-9]{4,32}$/.test(code)) {
      await noticeDialog({ title: 'Přístupový kód', message: 'Kód musí mít 4 až 32 znaků, jen písmena a číslice.' });
      return;
    }
    setSaving(true);
    try {
      await b2bApi.update(output.id, { offerTypes: output.kind === 'offer' ? form.types : undefined, publishedAt: form.publishedAt || null, accessCode: withCode && code !== output.accessCode ? code : undefined });
      await onSaved();
      onClose();
    } catch (err) {
      await noticeDialog({ title: 'Uložení se nepodařilo', message: errText(err) });
    } finally {
      setSaving(false);
    }
  };
  return (
    <DialogShell eyebrow={KIND_LABEL[output.kind]} title={label(output)} onClose={onClose}>
      <form className="mailing-dialog-body st-b2b-edit" onSubmit={(e) => void submit(e)}>
        {output.kind === 'offer' ? (
          <div className="mailing-dialog-field">
            <span>Typ nabídky</span>
            <div className="st-otype-opts">
              {typeSet.types.map((t) => <label key={t}><input type="checkbox" checked={form.types.includes(t)} onChange={() => toggle(t)} />{t}</label>)}
            </div>
          </div>
        ) : null}
        {output.kind === 'offer' && typeSet.modules.length ? (
          <div className="mailing-dialog-field">
            <span>Moduly {typeSet.moduleParent}</span>
            <div className={`st-otype-opts${modulesOn ? '' : ' is-disabled'}`}>
              {typeSet.modules.map((t) => <label key={t}><input type="checkbox" disabled={!modulesOn} checked={form.types.includes(t)} onChange={() => toggle(t)} />{t}</label>)}
            </div>
          </div>
        ) : null}
        <label className="mailing-dialog-field">
          <span>Publikováno</span>
          <input type="date" className="form-control" value={form.publishedAt} onChange={(e) => setForm((f) => ({ ...f, publishedAt: e.target.value }))} />
        </label>
        {withCode ? (
          <label className="mailing-dialog-field">
            <span>Přístupový kód</span>
            <span className="st-b2b-code-row">
              <input type="text" className="form-control st-b2b-code-input" value={form.accessCode} maxLength={32} autoComplete="off" spellCheck={false} onChange={(e) => setForm((f) => ({ ...f, accessCode: e.target.value.toUpperCase() }))} />
              <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setForm((f) => ({ ...f, accessCode: newAccessCode() }))}>Vygenerovat nový</button>
            </span>
            <small className="text-muted">Po změně musí klient zadat nový kód, starý přestane platit.</small>
          </label>
        ) : null}
        <div className="mailing-dialog-actions">
          <button type="button" className="btn btn-sm btn-outline-secondary" onClick={onClose}>Zrušit</button>
          <button type="submit" className="btn btn-sm btn-primary" disabled={saving}>Uložit</button>
        </div>
      </form>
    </DialogShell>
  );
}

function errText(e: unknown): string {
  return e instanceof ApiError ? e.message ?? e.code : String(e);
}

function fmtDate(v: string | null): string {
  if (!v) return '–';
  return new Intl.DateTimeFormat('cs-CZ', { day: 'numeric', month: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' }).format(new Date(v));
}

async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

interface Props {
  kind?: B2bKind;
  partnerId?: number;
  affiliateId?: number;
  leadId?: number;
  showKind?: boolean;
  showClient?: boolean;
  onEdit?: (o: B2bOutput) => void;
  actions?: ReactNode;
}

export function B2bVystupyTable({ kind, partnerId, affiliateId, leadId, showKind = false, showClient = true, onEdit, actions }: Props): JSX.Element {
  const isReportList = kind === 'report';
  const defaultStatus: '' | B2bStatus = partnerId || affiliateId || leadId ? '' : 'active';
  const [items, setItems] = useState<B2bOutput[] | null>(null);
  const [status, setStatus] = useState<'' | B2bStatus | 'deal'>(defaultStatus);
  const [type, setType] = useState<'' | B2bOfferType>('');
  const [editing, setEditing] = useState<B2bOutput | null>(null);
  const [q, setQ] = useState('');
  const [busy, setBusy] = useState<number | null>(null);
  const [views, setViews] = useState<{ id: number; title: string; rows: B2bView[] } | null>(null);
  useFeather([items, views, editing]);

  const load = useCallback(async () => {
    try {
      const rows = await b2bApi.list({ kind, partnerId, affiliateId, leadId, status: status || undefined, type: type || undefined, q: q.trim() || undefined });
      setItems(rows);
    } catch (e) {
      await noticeDialog({ title: 'Výstupy se nepodařilo načíst', message: errText(e) });
      setItems([]);
    }
  }, [kind, partnerId, affiliateId, leadId, status, type, q]);

  useEffect(() => {
    const t = setTimeout(() => void load(), q ? 250 : 0);
    return () => clearTimeout(t);
  }, [load, q]);

  const copy = async (o: B2bOutput) => {
    const ok = await copyText(o.url);
    await noticeDialog({ title: ok ? 'Odkaz zkopírován' : 'Odkaz', message: hasAccessCode(o) ? <>{o.url}<br />Přístupový kód: <strong>{o.accessCode}</strong></> : o.url });
  };

  const setState = async (o: B2bOutput, next: B2bStatus) => {
    if (next === 'archived') {
      const ok = await confirmDialog({ title: 'Archivovat výstup', message: `Odkaz „${label(o)}“ přestane fungovat a klient uvidí, že platnost vypršela. Pokračovat?`, confirmLabel: 'Archivovat', danger: true });
      if (!ok) return;
    }
    setBusy(o.id);
    try {
      await b2bApi.update(o.id, { status: next });
      await load();
    } catch (e) {
      await noticeDialog({ title: 'Změna se nepodařila', message: errText(e) });
    } finally {
      setBusy(null);
    }
  };

  const rotate = async (o: B2bOutput) => {
    const ok = await confirmDialog({ title: 'Nový odkaz', message: `Starý odkaz „${label(o)}“ přestane platit a vznikne nový. Pokračovat?`, confirmLabel: 'Vytvořit nový odkaz' });
    if (!ok) return;
    setBusy(o.id);
    try {
      const updated = await b2bApi.rotate(o.id);
      await load();
      await copyText(updated.url);
      await noticeDialog({ title: 'Nový odkaz vytvořen a zkopírován', message: updated.url });
    } catch (e) {
      await noticeDialog({ title: 'Nový odkaz se nepodařilo vytvořit', message: errText(e) });
    } finally {
      setBusy(null);
    }
  };

  const showViews = async (o: B2bOutput) => {
    try {
      setViews({ id: o.id, title: label(o), rows: await b2bApi.views(o.id) });
    } catch (e) {
      await noticeDialog({ title: 'Historii zobrazení se nepodařilo načíst', message: errText(e) });
    }
  };

  if (items === null) return <div className="st-empty">Načítám…</div>;
  const visibleItems = items.filter((i) => status || i.status !== 'archived');
  const showTypes = kind === 'offer' || (!kind && visibleItems.some((i) => i.kind === 'offer'));
  const selects: ServiceBarSelect[] = [
    ...(showTypes ? [{
      label: 'Typ',
      value: type,
      onChange: (value: string) => setType(value as '' | B2bOfferType),
      options: [{ value: '', label: 'Všechny typy' }, ...[...b2bOfferTypesFor().types, ...b2bOfferTypesFor().modules].map((value) => ({ value, label: value }))],
    }] : []),
    {
      label: 'Stav',
      value: status,
      onChange: (value: string) => setStatus(value as '' | B2bStatus | 'deal'),
      options: [
        { value: 'active', label: 'Aktivní' },
        { value: 'deal', label: 'Spolupráce' },
        { value: 'draft', label: 'Koncept' },
        { value: 'archived', label: 'Archiv' },
        { value: '', label: 'Vše kromě archivu' },
      ],
    },
  ];

  return (
    <ArcPage as="div" className={`st-b2b${isReportList ? ' st-b2b--reports' : ''}`}>
      <ServiceBar
        variant="seg"
        selects={selects}
        search={q}
        onSearch={setQ}
        searchPlaceholder="Hledat klienta"
        onReset={() => { setType(''); setStatus(defaultStatus); setQ(''); }}
        right={(
          <div className="st-news-service-actions">
            <span className="st-count"><strong>{visibleItems.length}</strong><span>{visibleItems.length === 1 ? 'výstup' : visibleItems.length > 1 && visibleItems.length < 5 ? 'výstupy' : 'výstupů'}</span></span>
            {actions}
          </div>
        )}
      />
      {visibleItems.length === 0 ? <div className="st-empty st-b2b-empty"><span className="feather-icon"><i data-feather="file-text" /></span><strong>Žádné výstupy</strong><small>Zkuste upravit hledání nebo vybraný stav.</small></div> : (
        <ArcTableFrame className="st-promo-table-shell" label="B2B výstupy"><table className="table table-sm st-table st-b2b-output-table">
          <thead><tr>{showKind ? <th>Typ</th> : null}{isReportList ? <th>Report</th> : showClient ? <th>Klient</th> : null}{showTypes ? <th>Typ nabídky</th> : null}<th>Jazyk</th><th>Publikováno</th><th>Stav</th><th>Zobrazení</th><th>Naposledy otevřeno</th><th className="st-action-column"><span className="visually-hidden">Akce</span></th></tr></thead>
          <tbody>
            {visibleItems.map((o) => (
              <tr key={o.id} className={o.status === 'archived' ? 'st-row-expired' : ''} onClick={() => window.open(b2bOpenUrl(o), '_blank', 'noopener')}>
                {showKind ? <td className="st-b2b-col-kind" data-label="Typ">{KIND_LABEL[o.kind]}{!showClient && o.kind === 'guide' ? <small className="d-block text-muted">{o.title}</small> : null}</td> : null}
                {isReportList ? (
                  <td className="st-b2b-col-title" data-label="Report">
                    <a className="st-b2b-report-title" href={b2bOpenUrl(o)} target="_blank" rel="noopener" onClick={(e) => e.stopPropagation()}>{o.title}</a>
                    {showClient && o.clientName ? <span className="st-b2b-report-client">{o.clientName}</span> : null}
                  </td>
                ) : showClient ? <td className="st-b2b-col-client text-nowrap" data-label="Klient"><strong>{o.clientName ?? o.title}</strong>{o.kind === 'guide' && o.clientName ? <small className="d-block text-muted fw-normal">{o.title}</small> : null}{hasAccessCode(o) ? <small className="d-block text-muted fw-normal">Kód: <span className="st-b2b-code">{o.accessCode}</span></small> : null}</td> : null}
                {showTypes ? <td className="st-b2b-col-types" data-label="Typ nabídky">{o.kind === 'offer' ? <OfferTypes types={o.offerTypes} /> : <span className="text-muted">{KIND_LABEL[o.kind]}</span>}</td> : null}
                <td className="st-b2b-col-language" data-label="Jazyk">{o.language.toUpperCase()}{o.currency ? ` · ${o.currency}` : ''}</td>
                <td className="st-b2b-col-published text-nowrap" data-label="Publikováno">{o.publishedAt ? fmtDay(o.publishedAt) : <span className="text-muted">nezadáno</span>}</td>
                <td className="st-b2b-col-status" data-label="Stav"><span className={`st-badge ${o.isDeal && o.status !== 'archived' ? 'deal' : o.status === 'active' ? 'pub' : o.status === 'archived' ? 'exp' : ''}`}>{o.isDeal && o.status !== 'archived' ? 'Spolupráce' : STATUS_LABEL[o.status]}</span></td>
                <td className="st-b2b-col-views" data-label="Zobrazení">{o.viewCount ? <button type="button" className="st-b2b-view-link" aria-label={`Historie zobrazení ${label(o)}: ${o.viewCount}`} onClick={(e) => { e.stopPropagation(); void showViews(o); }}>{o.viewCount}×</button> : '–'}</td>
                <td className="st-b2b-col-opened text-nowrap" data-label="Naposledy otevřeno">{fmtDate(o.lastViewedAt)}</td>
                <td className="st-b2b-col-actions">
                  <span className="st-akce">
                    <button type="button" disabled={busy === o.id} onClick={(e) => { e.stopPropagation(); void copy(o); }} title="Zkopírovat odkaz pro klienta" aria-label={`Zkopírovat odkaz ${label(o)}`}><i data-feather="link" /></button>
                    <button type="button" disabled={busy === o.id} onClick={(e) => { e.stopPropagation(); if (o.kind === 'guide') { if (onEdit) onEdit(o); else window.location.assign(`/vystupy/b2b/navody/${o.id}`); } else setEditing(o); }} title={o.kind === 'guide' ? 'Upravit návod' : hasAccessCode(o) ? 'Upravit typ, datum publikace a přístupový kód' : 'Upravit typ a datum publikace'} aria-label={`Upravit ${label(o)}`}><i data-feather="edit-2" /></button>
                    <button type="button" disabled={busy === o.id} onClick={(e) => { e.stopPropagation(); void rotate(o); }} title="Nový odkaz (starý přestane platit)" aria-label={`Nový odkaz ${label(o)}`}><i data-feather="refresh-cw" /></button>
                    {o.status === 'archived'
                      ? <button type="button" disabled={busy === o.id} onClick={(e) => { e.stopPropagation(); void setState(o, 'active'); }} title="Obnovit" aria-label={`Obnovit ${label(o)}`}><i data-feather="rotate-ccw" /></button>
                      : <button type="button" className="is-danger" disabled={busy === o.id} onClick={(e) => { e.stopPropagation(); void setState(o, 'archived'); }} title="Archivovat (odkaz přestane platit)" aria-label={`Archivovat ${label(o)}`}><i data-feather="archive" /></button>}
                    <button type="button" onClick={(e) => { e.stopPropagation(); window.open(b2bOpenUrl(o), '_blank', 'noopener'); }} title="Otevřít" aria-label={`Otevřít ${label(o)}`}>{isReportList ? 'Otevřít' : <i data-feather="chevron-right" />}</button>
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table></ArcTableFrame>
      )}
      {editing ? <EditDialog output={editing} onClose={() => setEditing(null)} onSaved={load} /> : null}
      {views ? (
        <ArcDialog title={views.title} description="Historie zobrazení výstupu" onClose={() => setViews(null)} size="medium">
          {views.rows.length === 0 ? <div className="st-b2b-views-empty">Zatím nikdo neotevřel.</div> : (
            <ul className="st-b2b-views-list">
              {views.rows.map((v, i) => <li key={i}><strong>{fmtDate(v.viewedAt)}</strong><span>{v.ip ?? 'Neznámá IP'}</span><small>{(v.userAgent ?? 'Neznámé zařízení').slice(0, 90)}</small></li>)}
            </ul>
          )}
        </ArcDialog>
      ) : null}
    </ArcPage>
  );
}
